import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, ArrowRight, Minus, Plus, Eye, Trash2, CheckCircle2, Circle } from 'lucide-react';
import InfoSpot from '@/components/InfoSpot';
import PortalShellV2 from '@/components/PortalShellV2';
import AutoCursor from '@/components/AutoCursor';

function Stepper({ active }) {
  const steps = ['Basic Details', 'Assign Roles', 'Review'];
  return (
    <div className="px-6 py-4 bg-[#F7F7F7] rounded-xl mb-6">
      <div className="flex items-center justify-between max-w-2xl mx-auto">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${
                i < active ? 'bg-teal-600 border-teal-600' :
                i === active ? 'bg-white border-teal-600' :
                'bg-white border-[#D1D5DB]'
              }`}>
                {i < active
                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  : i === active
                  ? <div className="w-1.5 h-1.5 rounded-full bg-teal-600" />
                  : null}
              </div>
              <span className={`text-[11.5px] font-body whitespace-nowrap ${i === active ? 'font-bold text-[#0A0A0A]' : i < active ? 'text-[#0A0A0A] font-semibold' : 'text-[#6B7280]'}`}>{s}</span>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-[2px] mx-2 mt-[-16px] ${i < active ? 'bg-teal-600' : 'bg-[#E5E7EB]'}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------- Role card (radio-style, expandable access chips) ---------- */
function RoleRow({ name, desc, selected, showAccess }) {
  return (
    <div className={`block w-full rounded-lg border p-3 mb-2.5 ${selected ? 'border-teal-600/40 bg-white' : 'border-[#E5E7EB] bg-white'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${selected ? 'border-teal-600' : 'border-[#D1D5DB]'}`}>
            {selected && <div className="w-1.5 h-1.5 rounded-full bg-teal-600" />}
          </div>
          <p className="text-[12.5px] font-body leading-snug">
            <span className="font-bold text-[#0A0A0A]">{name}</span>
            <span className="text-[#9CA3AF] mx-1.5">|</span>
            <span className="text-[#6B7280]">{desc}</span>
          </p>
        </div>
        {showAccess && (
          <button className="flex items-center gap-1 text-[11px] font-body text-teal-700 hover:text-teal-800 flex-shrink-0 whitespace-nowrap">
            <Eye size={11} /> View detailed permissions
          </button>
        )}
      </div>

      {showAccess && (
        <div className="mt-2 ml-6 space-y-1">
          <div className="flex items-start gap-1.5">
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-body font-semibold flex-shrink-0">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Full Access
            </span>
            <span className="text-[10.5px] font-body text-[#6B7280] leading-snug">Person register · Pharmacy requests · Prior requests · Support requests</span>
          </div>
          <div className="flex items-start gap-1.5">
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[10px] font-body font-semibold flex-shrink-0">
              <Circle size={9} />
              Partial access
            </span>
            <span className="text-[10.5px] font-body text-[#6B7280] leading-snug">Surveys · Reimbursement accounts · Transaction register</span>
          </div>
        </div>
      )}
    </div>
  );
}

function FacilityPanel({ name, expanded, selectedRole, pickAt }) {
  const [pickedRole, setPickedRole] = useState(null);
  useEffect(() => {
    if (!expanded || pickAt == null) return;
    const t = setTimeout(() => setPickedRole(selectedRole), pickAt * 1000);
    return () => clearTimeout(t);
  }, [expanded, selectedRole, pickAt]);

  if (!expanded) {
    return (
      <div className="rounded-xl bg-[#F7F7F7] px-5 py-4 mb-2.5 flex items-center justify-between">
        <h3 className="font-heading font-extrabold text-[14px] text-[#0A0A0A]">{name}</h3>
        <button className="w-5 h-5 rounded-full border border-[#D1D5DB] flex items-center justify-center text-[#6B7280]">
          <Plus size={11} />
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-[#F7F7F7] px-5 py-4 mb-2.5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-extrabold text-[14px] text-[#0A0A0A]">{name}</h3>
        <button className="w-5 h-5 rounded-full border border-[#D1D5DB] flex items-center justify-center text-[#6B7280]">
          <Minus size={11} />
        </button>
      </div>

      <div className="flex flex-col">
        <RoleRow name="Facility admin"    desc="Manages user access, roles and permissions" selected={pickedRole === 'facility'} showAccess />
        <RoleRow name="Operations admin"  desc="Ensures smooth running and updates"           selected={pickedRole === 'ops'}      showAccess />
        <RoleRow name="Regular user"      desc="Performs and views module-wise actions assigned" selected={pickedRole === 'regular'} />
      </div>

      <div className="flex justify-end mt-1">
        <button className="flex items-center gap-1 text-[11.5px] font-body text-red-500 hover:text-red-600">
          <Trash2 size={11} /> Delete
        </button>
      </div>
    </div>
  );
}

export default function UserFormRolesV2Scene({ onNext }) {
  const cursorPath = [
    { x: 400, y: 260, delay: 1.0 },
    { x: 220, y: 420, delay: 2.6 },     // pick Facility admin
    { x: 420, y: 720, delay: 2.2 },
    { x: 450, y: 820, delay: 1.6, click: true },
  ];

  return (
    <PortalShellV2 variant="headerOnly" overlay={<AutoCursor targets={cursorPath} startDelay={0.5} />}>
      <div data-testid="scene-user-form-roles-v2" className="px-10 pt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[11.5px] font-body text-[#6B7280] mb-1.5 flex items-center gap-1">
            Manage <ChevronRight size={10} className="inline" /> Users <ChevronRight size={10} className="inline" /> <span className="text-[#0A0A0A]">Add new user</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            className="font-heading font-extrabold text-[30px] leading-tight tracking-tight text-[#0A0A0A] flex items-center gap-2"
          >
            Add New User
            <InfoSpot
              text="Step 2 of 3 — Assign Roles. Configure per-facility roles: Facility admin, Operations admin, or Regular user."
              position="right"
              autoOpenAt={0.8}
              autoCloseAfter={2.5}
              glowAt={0.5}
            />
          </motion.h1>
          <p className="mt-0.5 font-body text-[12px] text-[#6B7280]">Create a new user and set access permissions</p>

          <div className="border-b border-[#E5E7EB] mt-4 mb-5" />

          <Stepper active={1} />

          <button className="flex items-center gap-1 text-[12px] font-body text-[#0A0A0A] mb-4 hover:text-teal-700">
            <ChevronLeft size={12} /> Back
          </button>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="font-heading font-extrabold text-[18px] text-[#0A0A0A]">Assign roles</h2>
            <p className="font-body text-[12px] text-[#6B7280] mb-4">Define what this user can access and manage</p>

            <FacilityPanel name="Rashid Hospital Location A" expanded selectedRole="facility" pickAt={3.0} />
            <FacilityPanel name="Rashid Hospital Location B" expanded={false} />
            <FacilityPanel name="Rashid Pharmacy"            expanded={false} />

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 5.5 }}
              className="flex justify-center mt-6"
            >
              <motion.button
                data-testid="wizard-next-roles-v2"
                onClick={onNext}
                animate={{
                  scale: [1, 1, 1.08, 0.97, 1.05, 1],
                  boxShadow: [
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 10px rgba(13,148,136,0.3)',
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 8px rgba(13,148,136,0.25)',
                    '0 0 0 0 rgba(13,148,136,0)',
                  ],
                }}
                transition={{ duration: 2.4, delay: 6.0, times: [0, 0.2, 0.4, 0.6, 0.8, 1] }}
                className="flex items-center gap-2 px-10 py-2.5 rounded-xl bg-teal-600 text-white font-body font-bold text-[12.5px] hover:bg-teal-700"
              >
                Next <ArrowRight size={13} />
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </PortalShellV2>
  );
}
